import React, { useState, useEffect } from 'react'
import { Box, Typography, Button, Alert } from '@mui/material'
import { useAuthInfo } from '@propelauth/react'
import AddIcon from '@mui/icons-material/Add'

import CustomTable from '../../../../components/CustomTable'
import { CustomTableSkeleton } from '../../../../components/CustomTable'
import { listUsersInOrganization } from '../../../../utils/utils'
import InviteUserModal from './modals/InviteUserModal'

function UsersAndPermissionsPage() {
    const [users, setUsers] = useState([])
    const [loading, setLoading] = useState(true)
    const [showSuccessAlert, setShowSuccessAlert] = useState(false)
    const [successMessage, setSuccessMessage] = useState('')
    const [inviteModalOpen, setInviteModalOpen] = useState(false)

    const authInfo = useAuthInfo()
    const orgs = authInfo.orgHelper.getOrgs()
    const orgId = orgs[0]?.orgId

    // Fetch users function
    const fetchUsers = async () => {
        if (!orgId) return

        setLoading(true)
        try {
            const orgAccessToken = await authInfo.tokens.getAccessTokenForOrg(orgId)
            const response = await listUsersInOrganization(orgId, orgAccessToken)

            console.log('listUsersInOrganization response:', response)

            // Handle different response formats
            let usersList = []
            if (Array.isArray(response)) {
                usersList = response
            } else if (response && Array.isArray(response.users)) {
                usersList = response.users
            } else if (response && typeof response === 'object') {
                usersList = Object.values(response).filter(item => item && item.user_id)
            }

            setUsers(usersList)
        } catch (error) {
            console.error('Error fetching users:', error)
            setUsers([])
        } finally {
            setLoading(false)
        }
    }

    // Fetch users on component mount
    useEffect(() => {
        fetchUsers()
    }, [orgId])

    // Handle successful invite
    const handleInviteSuccess = async () => {
        // Refresh user list
        await fetchUsers()

        // Show success message
        setSuccessMessage('Invitation sent successfully!')
        setShowSuccessAlert(true)
        setTimeout(() => setShowSuccessAlert(false), 3000)
    }

    // Get user's role from nested org structure
    const getUserRole = (user) => {
        if (user.role) {
            return user.role
        }

        if (user.org_id_to_org_info && user.org_id_to_org_info[orgId]) {
            const userOrg = user.org_id_to_org_info[orgId]
            const role = userOrg.user_role ||
                        (userOrg.inherited_user_roles_plus_current_role &&
                         userOrg.inherited_user_roles_plus_current_role[0])
            if (role) {
                return role
            }
        }

        return 'Viewer'
    }

    // Format user data for table
    const userRows = users.map((user) => ({
        id: user.user_id,
        email: user.email || 'N/A',
        permission: getUserRole(user),
    }))

    // Define table columns
    const tableColumns = [
        {
            field: 'email',
            header: 'Email',
            width: '60%',
        },
        {
            field: 'permission',
            header: 'Permission',
            width: '40%',
        },
    ]

    return (
        <Box sx={{
            width: '100%',
            height: '100%',
            margin: 0,
            padding: 0,
            boxSizing: 'border-box',
            overflowY: 'auto',
        }}>
            <Box sx={{
                width: '100%',
                height: '100%',
                padding: '40px 64px',
                boxSizing: 'border-box',
                gap: '24px',
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: '#F9FAFB',
            }}>
                {/* Header Section */}
                <Box sx={{
                    width: '100%',
                    margin: '0 auto',
                    display: 'flex',
                    flexDirection: 'column',
                    boxSizing: 'border-box',
                }}>
                    {showSuccessAlert && (
                        <Alert
                            severity="success"
                            sx={{
                                mb: 2,
                                maxWidth: 'fit-content',
                                marginLeft: 'auto',
                                marginRight: 'auto',
                            }}
                            onClose={() => setShowSuccessAlert(false)}
                        >
                            {successMessage}
                        </Alert>
                    )}

                    <Box sx={{
                        width: '100%',
                        margin: '0 auto',
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                    }}>
                        <Box sx={{
                            gap: '8px',
                            display: 'flex',
                            flexDirection: 'column'
                        }}>
                            <Typography sx={{
                                fontSize: '24px',
                                fontWeight: 700,
                                lineHeight: '36px',
                                color: '#374151',
                            }}>
                                Users & permissions
                            </Typography>
                            <Typography sx={{
                                fontSize: '14px',
                                fontWeight: 400,
                                lineHeight: '20px',
                                color: '#374151',
                            }}>
                                Manage users and their permissions in the workspace. <Box component="span" sx={{ color: '#2563EB', cursor: 'pointer' }}>See what each permission entails</Box>
                            </Typography>
                        </Box>

                        <Box sx={{
                            gap: '8px',
                            display: 'flex',
                            paddingBottom: '8px',
                            alignItems: 'flex-start',
                        }}>
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={() => setInviteModalOpen(true)}
                                sx={{
                                    backgroundColor: '#1F477D',
                                    color: 'white',
                                    height: '34px',
                                    borderRadius: '6px',
                                    fontSize: '12px',
                                    fontWeight: 500,
                                    textTransform: 'none',
                                    padding: '0 16px',
                                    '&:hover': {
                                        backgroundColor: '#2563EB',
                                    },
                                    '& .MuiButton-startIcon': {
                                        marginRight: '8px',
                                        '& svg': {
                                            fontSize: '16px',
                                        },
                                    },
                                }}
                            >
                                Invite
                            </Button>
                        </Box>
                    </Box>
                </Box>

                {/* Table Section */}
                {loading ? (
                    <CustomTableSkeleton
                        columns={tableColumns}
                        backgroundColor={'#F9FAFB'}
                    />
                ) : (
                    <Box sx={{ height: '100%' }}>
                        <CustomTable
                            columns={tableColumns}
                            data={userRows}
                            backgroundColor={'#F9FAFB'}
                            menuColumn={true}
                            searchBar={true}
                            pagination={true}
                        />
                    </Box>
                )}

                {/* Invite User Modal */}
                <InviteUserModal
                    open={inviteModalOpen}
                    onClose={() => setInviteModalOpen(false)}
                    onInviteSuccess={handleInviteSuccess}
                />
            </Box>
        </Box>
    )
}

export default UsersAndPermissionsPage
